import { useEffect, useRef } from "react";

const NEPAL = { lat: 28.0, lon: 84.0 };

const CITIES = [
  { lat: 40.7,  lon: -74.0,  label: "New York" },
  { lat: 51.5,  lon: -0.1,   label: "London" },
  { lat: 35.7,  lon: 139.7,  label: "Tokyo" },
  { lat: 1.3,   lon: 103.8,  label: "Singapore" },
  { lat: 25.2,  lon: 55.3,   label: "Dubai" },
  { lat: 48.9,  lon: 2.3,    label: "Paris" },
  { lat: 52.5,  lon: 13.4,   label: "Berlin" },
  { lat: 37.6,  lon: -122.4, label: "San Francisco" },
  { lat: -33.9, lon: 151.2,  label: "Sydney" },
  { lat: 55.8,  lon: 37.6,   label: "Moscow" },
  { lat: 39.9,  lon: 116.4,  label: "Beijing" },
  { lat: 19.1,  lon: 72.9,   label: "Mumbai" },
  { lat: -23.5, lon: -46.6,  label: "São Paulo" },
  { lat: 22.3,  lon: 114.2,  label: "Hong Kong" },
  { lat: 13.8,  lon: 100.5,  label: "Bangkok" },
  { lat: -1.3,  lon: 36.8,   label: "Nairobi" },
  { lat: 41.0,  lon: 28.9,   label: "Istanbul" },
  { lat: 43.7,  lon: -79.4,  label: "Toronto" },
  { lat: 34.1,  lon: -118.2, label: "Los Angeles" },
  { lat: 28.6,  lon: 77.2,   label: "Delhi" },
];

const MESSAGES = [
  "one film can connect a generation",
  "18,000 voices. one cinema.",
  "guff that crosses every border",
  "from the hills of Nepal to everywhere",
  "real conversations. no filters.",
  "your next favourite film is waiting",
  "nepali cinema finding its world",
  "debates that need no passport",
  "watch together, even apart",
];

function project(lon, lat, W, H) {
  const lonShift = -10;
  const x = (lon - lonShift + 180) / 360 * W;
  const latRad = lat * Math.PI / 180;
  const mercN  = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  // On portrait screens (H >= W) scale the map vertically so continents fill the
  // canvas height at the same proportion as on a typical desktop canvas.
  const vScale = H >= W ? 0.55 * (H / W) * 2.34 : 0.55;
  const y = (H / 2) - (W * mercN / (2 * Math.PI)) * vScale + H * 0.05;
  return { x, y };
}

function bezierCtrl(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dist = Math.sqrt((x2-x1)**2 + (y2-y1)**2);
  return { cx: mx, cy: my - dist * 0.25 };
}

function bezierPoint(x1, y1, cx, cy, x2, y2, t) {
  const mt = 1 - t;
  return {
    x: mt*mt*x1 + 2*mt*t*cx + t*t*x2,
    y: mt*mt*y1 + 2*mt*t*cy + t*t*y2,
  };
}

export default function GlobeHero({ onExplore, onJoinDiscussion }) {
  const canvasRef = useRef(null);
  const chipsRef  = useRef(null);
  const rafRef    = useRef(null);

  // Floating chips
  useEffect(() => {
    if (!chipsRef.current) return;
    let mi = 0, pi = 0;
    const positions = [
      { left: "55%", top: "8%"  },
      { left: "80%", top: "20%" },
      { left: "68%", top: "72%" },
      { left: "85%", top: "55%" },
      { left: "60%", top: "45%" },
      { left: "90%", top: "38%" },
    ];
    function show() {
      const el = chipsRef.current;
      if (!el) return;
      const pos = positions[pi % positions.length]; pi++;
      const msg = MESSAGES[mi % MESSAGES.length]; mi++;
      const chip = document.createElement("div");
      chip.style.cssText = `position:absolute;left:${pos.left};top:${pos.top};transform:translateY(8px);background:rgba(4,4,10,0.92);border:0.5px solid rgba(255,92,53,0.22);border-radius:20px;padding:5px 14px;font-family:'Plus Jakarta Sans',sans-serif;font-size:9.5px;color:#7a7a9a;white-space:nowrap;opacity:0;transition:opacity 0.5s,transform 0.5s;pointer-events:none;backdrop-filter:blur(8px);z-index:10;`;
      chip.textContent = msg;
      el.appendChild(chip);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        chip.style.opacity = "1"; chip.style.transform = "translateY(0)";
      }));
      setTimeout(() => {
        chip.style.opacity = "0"; chip.style.transform = "translateY(-8px)";
        setTimeout(() => chip.remove(), 500);
      }, 3200);
    }
    const t = setTimeout(show, 1000);
    const iv = setInterval(show, 2800);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let disposed = false;

    async function init() {
      const geoRes = await fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
      const topo   = await geoRes.json();

      const topoScript = document.createElement("script");
      topoScript.src   = "https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js";
      document.head.appendChild(topoScript);
      await new Promise(res => { topoScript.onload = res; });
      if (disposed) return;

      const countries = window.topojson.feature(topo, topo.objects.countries);

      function resize() {
        const p = canvas.parentElement;
        canvas.width  = p.clientWidth;
        canvas.height = p.clientHeight;
      }
      resize();
      window.addEventListener("resize", resize);

      // ── Two dots per route: one outbound, one inbound, offset by 0.5 ──────
      const travelers = [];
      CITIES.forEach((city, i) => {
        const base  = (i / CITIES.length);
        const speed = 0.0016 + (i % 7) * 0.00022;

        // Outbound: Nepal → City (t goes 0→1)
        travelers.push({ city, t: base % 1,       speed, direction: 1 });
        // Inbound:  City → Nepal (t goes 0→1 but drawn reversed)
        travelers.push({ city, t: (base + 0.5) % 1, speed, direction: -1 });
      });

      function drawDot(x, y, fade, color = "255,92,53") {
        // Outer glow
        const g = ctx.createRadialGradient(x, y, 0, x, y, 9);
        g.addColorStop(0, `rgba(${color},${0.45 * fade})`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.beginPath();
        ctx.arc(x, y, 9, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
        // Core
        ctx.beginPath();
        ctx.arc(x, y, 2.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color},${0.95 * fade})`;
        ctx.fill();
        // White center
        ctx.beginPath();
        ctx.arc(x, y, 1.1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.88 * fade})`;
        ctx.fill();
      }

      function draw() {
        if (disposed) return;
        rafRef.current = requestAnimationFrame(draw);
        const W = canvas.width, H = canvas.height;
        ctx.clearRect(0, 0, W, H);

        // Background
        const bg = ctx.createRadialGradient(W*0.62, H*0.45, 0, W*0.62, H*0.45, W*0.72);
        bg.addColorStop(0,   "#0f0c22");
        bg.addColorStop(0.5, "#08060f");
        bg.addColorStop(1,   "#040308");
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, W, H);

        // Dot grid
        ctx.fillStyle = "rgba(80,75,160,0.11)";
        for (let gx = 0; gx < W; gx += 18) {
          for (let gy = 0; gy < H; gy += 18) {
            ctx.beginPath(); ctx.arc(gx, gy, 0.8, 0, Math.PI*2); ctx.fill();
          }
        }

        // Grid lines
        ctx.strokeStyle = "rgba(70,65,140,0.09)";
        ctx.lineWidth   = 0.5;
        for (let lat = -60; lat <= 80; lat += 30) {
          const { y } = project(0, lat, W, H);
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
        }
        for (let lon = -180; lon <= 180; lon += 40) {
          const { x } = project(lon, 0, W, H);
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
        }

        // Countries
        countries.features.forEach(f => {
          ctx.beginPath();
          function drawPoly(coords) {
            coords.forEach(ring => {
              ring.forEach(([lon, lat], i) => {
                const { x, y } = project(lon, lat, W, H);
                i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
              });
              ctx.closePath();
            });
          }
          if (f.geometry.type === "Polygon") drawPoly(f.geometry.coordinates);
          else if (f.geometry.type === "MultiPolygon") f.geometry.coordinates.forEach(p => drawPoly(p));
          ctx.fillStyle   = "rgba(28,26,52,0.88)";
          ctx.fill();
          ctx.strokeStyle = "rgba(85,80,168,0.32)";
          ctx.lineWidth   = 0.6;
          ctx.stroke();
        });

        // Nepal + city positions
        const np = project(NEPAL.lon, NEPAL.lat, W, H);

        // ── Full arc lines (faint persistent base) ──────────────────────────
        CITIES.forEach(city => {
          const cp  = project(city.lon, city.lat, W, H);
          const ctl = bezierCtrl(np.x, np.y, cp.x, cp.y);
          ctx.beginPath();
          ctx.moveTo(np.x, np.y);
          ctx.quadraticCurveTo(ctl.cx, ctl.cy, cp.x, cp.y);
          ctx.strokeStyle = "rgba(255,92,53,0.12)";
          ctx.lineWidth   = 0.8;
          ctx.stroke();
        });

        // ── Traveling dots (bidirectional) ──────────────────────────────────
        travelers.forEach(tr => {
          tr.t = (tr.t + tr.speed) % 1;

          const cp  = project(tr.city.lon, tr.city.lat, W, H);
          const ctl = bezierCtrl(np.x, np.y, cp.x, cp.y);

          // direction 1  → Nepal to City (t: 0→1)
          // direction -1 → City to Nepal (t: 1→0, i.e. reversed)
          const bt = tr.direction === 1 ? tr.t : 1 - tr.t;
          const pt = bezierPoint(np.x, np.y, ctl.cx, ctl.cy, cp.x, cp.y, bt);

          // Fade in/out smoothly at both ends
          const fade = Math.sin(tr.t * Math.PI);

          drawDot(pt.x, pt.y, fade);
        });

        // ── City dots ───────────────────────────────────────────────────────
        CITIES.forEach(city => {
          const cp = project(city.lon, city.lat, W, H);
          // Pulse ring
          const pulse = (Date.now() % 2400) / 2400;
          ctx.beginPath();
          ctx.arc(cp.x, cp.y, 3 + pulse * 5, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,92,53,${0.3 * (1 - pulse)})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
          // Dot
          ctx.beginPath();
          ctx.arc(cp.x, cp.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,110,65,0.7)";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(cp.x, cp.y, 0.9, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,200,170,0.9)";
          ctx.fill();
        });

        // ── Nepal origin ─────────────────────────────────────────────────────
        const pulse = (Date.now() % 1800) / 1800;
        // Rings
        [1, 0.6, 0.3].forEach((opacity, i) => {
          const r = 8 + pulse * 16 + i * 6;
          ctx.beginPath();
          ctx.arc(np.x, np.y, r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,92,53,${opacity * (1 - pulse) * 0.5})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        });
        // Glow
        const ng = ctx.createRadialGradient(np.x, np.y, 0, np.x, np.y, 18);
        ng.addColorStop(0, "rgba(255,92,53,0.55)");
        ng.addColorStop(1, "rgba(255,92,53,0)");
        ctx.beginPath();
        ctx.arc(np.x, np.y, 18, 0, Math.PI * 2);
        ctx.fillStyle = ng;
        ctx.fill();
        // Core dot
        ctx.beginPath();
        ctx.arc(np.x, np.y, 5.5, 0, Math.PI * 2);
        ctx.fillStyle = "#FF5C35";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(np.x, np.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "#fff";
        ctx.fill();
        // Label
        ctx.font      = "700 9px 'Bricolage Grotesque', sans-serif";
        ctx.fillStyle = "#FF5C35";
        ctx.fillText("Kathmandu", np.x + 9, np.y - 8);

        // ── Vignettes ────────────────────────────────────────────────────────
        const lv = ctx.createLinearGradient(0, 0, W * 0.52, 0);
        lv.addColorStop(0,    "rgba(4,3,8,1)");
        lv.addColorStop(0.36, "rgba(4,3,8,0.88)");
        lv.addColorStop(0.54, "rgba(4,3,8,0.08)");
        lv.addColorStop(1,    "rgba(4,3,8,0)");
        ctx.fillStyle = lv;
        ctx.fillRect(0, 0, W, H);

        const tv = ctx.createLinearGradient(0, 0, 0, H);
        tv.addColorStop(0,    "rgba(4,3,8,0.85)");
        tv.addColorStop(0.12, "rgba(4,3,8,0)");
        tv.addColorStop(0.88, "rgba(4,3,8,0)");
        tv.addColorStop(1,    "rgba(4,3,8,0.85)");
        ctx.fillStyle = tv;
        ctx.fillRect(0, 0, W, H);
      }

      draw();
      return () => window.removeEventListener("resize", resize);
    }

    let cleanupFn;
    init().then(fn => { cleanupFn = fn; }).catch(console.error);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafRef.current);
      if (cleanupFn) cleanupFn();
    };
  }, []);

  return (
    <div style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      overflowX: "hidden",
    }}>
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
      />

      <div ref={chipsRef} className="globe-chips" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 }} />

      <div style={{
        position: "absolute", inset: 0, zIndex: 5,
        display: "flex", alignItems: "center",
      }}>
        <div className="globe-cta-inner" style={{ width: "100%", maxWidth: 1280, margin: "0 auto", padding: "0 clamp(16px, 4vw, 20px)" }}>
        <div style={{ maxWidth: "min(520px, 90vw)" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,92,53,0.1)", border: "0.5px solid rgba(255,92,53,0.28)",
            borderRadius: 20, padding: "5px 14px", marginBottom: 26,
            fontSize: 10.5, color: "#FF5C35", fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            letterSpacing: "0.07em", textTransform: "uppercase",
          }}>
            <span style={{ fontSize: 7 }}>●</span> nepal's movie community
          </div>

          <h1 className="globe-h1" style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: "clamp(32px, 8vw, 62px)", fontWeight: 800, color: "#f2f2f4",
            lineHeight: 1.0, letterSpacing: "clamp(-1px, -0.045em, -2.8px)", marginBottom: 20,
          }}>
            watch. rate.<br />
            <span style={{
              background: "linear-gradient(135deg, #FF5C35 0%, #ff9a7a 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>guff garoom.</span>
          </h1>

          <p className="globe-tagline" style={{
            fontSize: "clamp(13px, 3.5vw, 15px)", color: "#6a6a8a", lineHeight: 1.75,
            maxWidth: 400, marginBottom: 34,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>
All films. A world that loves them. Join 18,000 people keeping the conversation alive — wherever you are.
          </p>

          <div className="globe-cta-btns" style={{ display: "flex", gap: 11, marginBottom: 48, flexWrap: "wrap" }}>
            <button onClick={onExplore} style={{
              background: "linear-gradient(135deg, #FF5C35, #ff7a58)",
              color: "#fff", padding: "12px 28px", borderRadius: 22,
              fontSize: 13, fontWeight: 700,
              fontFamily: "'Bricolage Grotesque', sans-serif",
              letterSpacing: "0.02em", boxShadow: "0 4px 22px rgba(255,92,53,0.38)",
              border: "none", cursor: "pointer",
            }}>explore movies</button>
            <button onClick={onJoinDiscussion} style={{
              background: "rgba(255,255,255,0.04)", color: "#9898a6",
              border: "0.5px solid rgba(255,255,255,0.1)",
              padding: "12px 24px", borderRadius: 22,
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              backdropFilter: "blur(6px)",
            }}>join the guff</button>
          </div>

          <div className="globe-stats" style={{ display: "flex", gap: 34 }}>
            {[["400+","movies"],["18k","members"],["94k","reviews"],["free","always"]].map(([n, l]) => (
              <div key={l}>
                <div style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: 21, fontWeight: 800, color: "#f2f2f4", letterSpacing: "-0.5px",
                }}>{n}</div>
                <div style={{ fontSize: 11, color: "#42424e", marginTop: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}